import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';

import { Auth } from '@aws-amplify/auth';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import {
  catchError,
  exhaustMap,
  map,
  of,
  switchMap,
  tap,
  withLatestFrom,
} from 'rxjs';

import {
  MerchantViewModel,
  SiteContextListResponse,
  User,
} from '@app/api/models';
import {
  MeControllerService,
  MerchantsService,
  UsersService,
} from '@app/api/services';
import { UserService } from '@authentication/services/user.service';
import { MerchantActions } from '@merchant/store/merchant.slice';
import { GlobalLoaderService } from '@shared/components/global-loader/global-loader.service';

import {
  AuthenticationActions,
  AuthenticationSelectors,
} from './authentication.slice';

@Injectable()
export class AuthenticationEffects {
  /* REFRESH TOKEN */
  refreshToken$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthenticationActions.refreshToken.trigger),
      tap(() => {
        this.loaderService.incrementXhrNumber();
      }),
      switchMap(() => Auth.currentAuthenticatedUser({ bypassCache: true })),
      map(() => {
        this.store.dispatch(AuthenticationActions.getUserInfo.trigger());
        this.loaderService.decrementXhrNumber();
        return AuthenticationActions.refreshToken.success();
      })
    )
  );

  /* GET CHOOSABLE SITES */

  getChoosableContexts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthenticationActions.getChoosableContexts.trigger),
      exhaustMap(() =>
        this.meService.getSiteContexts().pipe(
          map((response: Array<SiteContextListResponse>) => {
            return AuthenticationActions.getChoosableContexts.success({
              response: response,
            });
          }),
          catchError((errorResponse: HttpErrorResponse) =>
            of(
              AuthenticationActions.getChoosableContexts.failure(
                errorResponse.error
              )
            )
          )
        )
      )
    )
  );

  /* GET CHOOSABLE SITES */

  getUserInfo$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthenticationActions.getUserInfo.trigger),
      exhaustMap(() =>
        this.userServive.getCurrentUserInfo().pipe(
          map((response: any) => {
            return AuthenticationActions.getUserInfo.success({
              response: response,
            });
          }),
          catchError((errorResponse: HttpErrorResponse) =>
            of(AuthenticationActions.getUserInfo.failure(errorResponse.error))
          )
        )
      )
    )
  );

  /* REGISTER MERCHANT */
  contextSwitch$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthenticationActions.contextSwitch.trigger),
      switchMap((action) =>
        this.meService.updateSiteContext({ body: action.data }).pipe(
          map(() => {
            this.store.dispatch(AuthenticationActions.refreshToken.trigger());
            return AuthenticationActions.contextSwitch.success();
          }),
          catchError((errorResponse: HttpErrorResponse) =>
            of(AuthenticationActions.contextSwitch.failure(errorResponse.error))
          )
        )
      )
    )
  );

  /* REGISTER MERCHANT */
  registerMerchant$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthenticationActions.registerMerchant.trigger),
      exhaustMap((action) =>
        this.merchantsService.registerMerchant({ body: action.data }).pipe(
          map((response: MerchantViewModel) => {
            this.router.navigate(['/auth/merchant-user-registration']);
            return AuthenticationActions.registerMerchant.success({ response });
          }),
          catchError((errorResponse: HttpErrorResponse) =>
            of(
              AuthenticationActions.registerMerchant.failure(
                errorResponse.error
              )
            )
          )
        )
      )
    )
  );

  // Telesales step 1
  registerMerchantByTelesales$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthenticationActions.registerMerchantByTelesales.trigger),
      exhaustMap((action) =>
        this.merchantsService.registerMerchant({ body: action.data }).pipe(
          map((response: MerchantViewModel) => {
            this.router.navigate(['/telesales/partner-user-registration']);
            return AuthenticationActions.registerMerchant.success({ response });
          }),
          catchError((errorResponse: HttpErrorResponse) =>
            of(
              AuthenticationActions.registerMerchant.failure(
                errorResponse.error
              )
            )
          )
        )
      )
    )
  );

  /* REGISTER MERCHANT USER */
  registerMerchantUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthenticationActions.registerMerchantUser.trigger),
      exhaustMap((action) =>
        this.usersServive.registerMerchantRootUser({ body: action.user }).pipe(
          map((user: User) =>
            AuthenticationActions.registerMerchantUser.success({
              user,
            })
          ),
          catchError((errorResponse: HttpErrorResponse) =>
            of(
              AuthenticationActions.registerMerchantUser.failure(
                errorResponse.error
              )
            )
          )
        )
      )
    )
  );

  // Telesales step 2
  registerMerchantUserByTelesales$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthenticationActions.registerMerchantUserByTelesales.trigger),
      exhaustMap((action) =>
        this.usersServive.registerMerchantRootUser({ body: action.user }).pipe(
          map((user: User) => {
            this.store.dispatch(MerchantActions.loadBasicDataById.trigger());
            this.router.navigate(['/telesales/partner-basic-data']);
            return AuthenticationActions.registerMerchantUserByTelesales.success(
              {
                user,
              }
            );
          }),
          catchError((errorResponse: HttpErrorResponse) =>
            of(
              AuthenticationActions.registerMerchantUserByTelesales.failure(
                errorResponse.error
              )
            )
          )
        )
      )
    )
  );

  constructor(
    private usersServive: UsersService,
    private userServive: UserService,
    private merchantsService: MerchantsService,
    private meService: MeControllerService,
    private router: Router,
    private store: Store,
    private actions$: Actions,
    private loaderService: GlobalLoaderService
  ) {}
}
